import './lineReveal.scss';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
// The circle's `background` is `currentColor` — the same black as the link
// text sitting on top of it (see lineReveal.scss). Animating all the way to
// opacity 1 turns it into a solid black disc, which swallows any letters it
// overlaps (black text on a solid black circle is simply invisible,
// regardless of paint order) — client-flagged regression. Capped back to a
// translucent peak so the text stays legible through it.
const CIRCLE_PEAK_OPACITY = .14;
// Hover speed, shared by every [data-line-hover] element so links and the
// approach principles stay in step. Cut again on client request - the disc
// used to take .7s to rise, which read as sluggish next to the scale.
const CIRCLE_RISE = .42;
const CIRCLE_FADE = .12;
const HOVER_SCALE_IN = .24;
const HOVER_SCALE_OUT = .2;
// Large enough to read as a genuine slide-in (per docs/gsap-animation-spec.md
// §3 — the reference video moves the work-listing view-more link/rule several
// hundred px, not a small nudge).
const CONTENT_OFFSET = 240;

// Shared "link/heading + line" system used by every `*__header`, `*__footer`,
// and `*__related-*` rule-and-link pairing across the theme (work listing,
// news listing, single-post related block, footer nav). Driven entirely by
// data attributes so new instances don't need new JS:
//
//   [data-line-group][data-line-side="left|right"]   wraps one pairing
//     [data-line-rule]      the rule/bar that grows in
//     [data-line-content]   the link or heading that slides in alongside it
//
//   [data-slide-in="left|right"]   a solo text/heading entrance, no rule
//
//   [data-line-hover]               opt into the hover scale + circle
//     [data-circle-from="top"]      circle rises from above instead of below
function attachHoverCircle(link) {
  const fromTop = link.dataset.circleFrom === 'top';
  const startY = fromTop ? -100 : 100;

  let circle = link.querySelector(':scope > .line-hover-circle');
  if (!circle) {
    circle = document.createElement('span');
    circle.className = 'line-hover-circle';
    circle.setAttribute('aria-hidden', 'true');
    link.prepend(circle);
  }

  const computedPosition = window.getComputedStyle(link).position;
  if (computedPosition === 'static') {
    link.style.position = 'relative';
    link.style.zIndex = 0;
  }

  gsap.set(circle, {opacity: 0, y: startY});

  const mm = gsap.matchMedia();

  mm.add(HOVER_QUERY, () => {
    // Client spec: y 100->0 (footer-nav: -100->0), opacity starting together
    // with the y tween ('<'). Overall duration cut ~2.5x (client: "the whole
    // animation... about 2 sec, make it around .7") — the 1s/.2s pair below
    // was still too slow as a whole. Hover-out is a literal reverse of this
    // same timeline, not a separate animation.
    const tl = gsap.timeline({paused: true})
        .to(circle, {y: 0, duration: CIRCLE_RISE, ease: 'power2.out'})
        .to(circle, {opacity: CIRCLE_PEAK_OPACITY, duration: CIRCLE_FADE, ease: 'power1.out'}, '<');

    const hoverIn = () => {
      gsap.to(link, {scale: 1.04, duration: HOVER_SCALE_IN, ease: 'power2.out'});
      tl.play();
    };
    const hoverOut = () => {
      gsap.to(link, {scale: 1, duration: HOVER_SCALE_OUT, ease: 'power2.out'});
      tl.reverse();
    };

    link.addEventListener('mouseenter', hoverIn);
    link.addEventListener('mouseleave', hoverOut);
    link.addEventListener('focus', hoverIn);
    link.addEventListener('blur', hoverOut);

    return () => {
      link.removeEventListener('mouseenter', hoverIn);
      link.removeEventListener('mouseleave', hoverOut);
      link.removeEventListener('focus', hoverIn);
      link.removeEventListener('blur', hoverOut);
      tl.kill();
    };
  });
}

function initHoverLinks(reducedMotion) {
  if (reducedMotion) return;

  gsap.utils.toArray('[data-line-hover]').forEach(attachHoverCircle);
}

function initLineGroups(reducedMotion) {
  gsap.utils.toArray('[data-line-group]').forEach((group) => {
    const rule = group.querySelector('[data-line-rule]');
    const content = group.querySelector('[data-line-content]');
    const side = group.dataset.lineSide === 'right' ? 'right' : 'left';
    const isLink = content && content.tagName === 'A';

    if (!rule && !content) return;

    if (reducedMotion) {
      return;
    }

    // One rule, one mechanism, direction is the only variable: `side` names
    // the physical edge the rule sits on/grows from. A link is "pushed" by
    // its rule — both anchored on `side`, the rule's growing tip carrying
    // the link along with it — regardless of which one happens to render
    // first in the DOM (work-listing__footer: rule then link;
    // news-listing__footer/bridge-contact-action: link then rule — same
    // animation, `side` is what differs, not the mechanism). A headline-only
    // pair instead has the rule anchor the OPPOSITE edge, so its tip grows
    // TOWARD the text and the two converge/meet instead of traveling
    // together.
    const ruleOrigin = isLink ? side : (side === 'left' ? 'right' : 'left');
    let offset = side === 'right' ? CONTENT_OFFSET : -CONTENT_OFFSET;

    if (isLink && rule && content) {
      // Measured BEFORE any transform is applied, while both still sit in
      // their natural resting layout: the link starts exactly at the rule's
      // own anchor edge (`side`) — where the line's growth begins — and
      // travels the real distance to its resting spot, not a guessed fixed
      // number, so it reads as the line's tip physically carrying it in.
      const ruleRect = rule.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      offset = side === 'right'
        ? ruleRect.right - contentRect.right
        : ruleRect.left - contentRect.left;
    }

    if (rule) gsap.set(rule, {scaleX: 0, transformOrigin: ruleOrigin});
    if (content) gsap.set(content, {x: offset, autoAlpha: 0});

    ScrollTrigger.create({
      trigger: group,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        const tl = gsap.timeline();
        if (rule) tl.to(rule, {scaleX: 1, duration: .6, ease: 'power2.out'}, 0);
        if (content) {
          // Same opacity-quicker-than-movement split as the hover circle:
          // the content is fully visible well before its slide finishes, so
          // the movement itself stays visible instead of hiding under the fade.
          tl.to(content, {autoAlpha: 1, duration: .3, ease: 'power1.out'}, 0);
          tl.to(content, {x: 0, duration: .6, ease: 'power2.out'}, 0);
        }
      },
    });
  });
}

function initSoloSlides(reducedMotion) {
  if (reducedMotion) return;

  gsap.utils.toArray('[data-slide-in]').forEach((el) => {
    const side = el.dataset.slideIn === 'right' ? 'right' : 'left';
    const offset = side === 'right' ? CONTENT_OFFSET : -CONTENT_OFFSET;

    gsap.set(el, {x: offset, autoAlpha: 0});

    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(el, {autoAlpha: 1, duration: .3, ease: 'power1.out'});
        gsap.to(el, {x: 0, duration: .6, ease: 'power2.out'});
      },
    });
  });
}

export function lineReveal() {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  initLineGroups(reducedMotion);
  initSoloSlides(reducedMotion);
  initHoverLinks(reducedMotion);
}
